import { useAchievements } from "api/achievements";
import { useCampaigns } from "api/campaigns";
import { useEvents } from "api/events";
import { useReward } from "api/rewards";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { formatImportedAchievements } from "components/Layout/Achievement/AchievementImportList/utils";
import { formatImportedCampaigns } from "components/Layout/Campaign/Direct/CampaignImportList/utils";
import { Imported } from "components/Layout/GlobalManagement/ConfigDuplication/ConfigImportList";
import { Tab } from "components/Layout/GlobalManagement/ConfigDuplication/Tabs/Tabs";
import { ImportResults } from "components/Layout/GlobalManagement/ConfigDuplication/utils";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { CampaignBEData } from "types/campaigns";

export const useGlobalManagement = () => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { addWalletTypeBatch } = useWallets();
  const { addCustomEventSchemaBatch } = useEvents();
  const { addRewardBatch } = useReward();
  const { addAchievementBatch } = useAchievements();
  const { addCampaignBatch } = useCampaigns();

  const addGlobalConfigBatch = async (resources: { [key in Tab]?: any[] }) => {
    if (!code) return;

    const wallets = resources[Tab.WALLET];
    const customEventSchema = resources[Tab.CUSTOM_EVENT];
    const rewards = resources[Tab.REWARDS];
    const achievements = resources[Tab.ACHIEVEMENTS];
    const campaigns = resources[Tab.CAMPAIGNS];
    const referrals = resources[Tab.REFERRALS];
    const automations = resources[Tab.AUTOMATIONS];

    // KEEP ONLY FORM VALUES
    const importResults: Imported = {
      ...(wallets ? { [Tab.WALLET]: [] } : {}),
      ...(customEventSchema ? { [Tab.CUSTOM_EVENT]: [] } : {}),
      ...(rewards ? { [Tab.REWARDS]: [] } : {}),
      ...(achievements ? { [Tab.ACHIEVEMENTS]: [] } : {}),
      ...(campaigns
        ? {
            [Tab.CAMPAIGNS]: [],
          }
        : {}),
      ...(referrals
        ? {
            [Tab.REFERRALS]: [],
          }
        : {}),
      ...(automations
        ? {
            [Tab.AUTOMATIONS]: [],
          }
        : {}),
    };
    // KEEP SORTED VALUES WITH ERRORS
    const sortedResults: ImportResults = {
      [Tab.WALLET]: {
        saved: [],
        unsaved: [],
      },
      [Tab.CUSTOM_EVENT]: {
        saved: [],
        unsaved: [],
      },
      [Tab.REWARDS]: {
        saved: [],
        unsaved: [],
      },
      [Tab.ACHIEVEMENTS]: {
        saved: [],
        unsaved: [],
      },
      [Tab.CAMPAIGNS]: {
        saved: [],
        unsaved: [],
      },
      [Tab.REFERRALS]: { saved: [], unsaved: [] },
      [Tab.AUTOMATIONS]: {
        saved: [],
        unsaved: [],
      },
    };

    wallets &&
      (await addWalletTypeBatch(wallets).then((resp) => {
        resp?.forEach((wallet, index) => {
          if ("walletTypeId" in wallet) {
            importResults[Tab.WALLET]?.push({
              ...wallet.data,
              walletTypeId: wallet.walletTypeId,
            });
            sortedResults[Tab.WALLET]?.saved.push({
              ...wallet.data,
              walletTypeId: wallet.walletTypeId,
            });
          } else {
            importResults[Tab.WALLET]?.push({
              ...wallet.data,
            });
            sortedResults[Tab.WALLET]?.unsaved.push({
              ...wallet,
              importedId: index,
            });
          }
        });
      }));

    customEventSchema &&
      (await addCustomEventSchemaBatch(
        customEventSchema.map((schema) => ({ ...schema, active: false })),
      ).then((resp) => {
        resp?.forEach((customEventSchema, index) => {
          importResults[Tab.CUSTOM_EVENT]?.push(customEventSchema.data);

          if (customEventSchema.data.isSaved) {
            sortedResults[Tab.CUSTOM_EVENT]?.saved.push({
              ...customEventSchema.data,
            });
          } else {
            sortedResults[Tab.CUSTOM_EVENT]?.unsaved.push({
              ...customEventSchema,
              importedId: index,
            });
          }
        });
      }));

    rewards &&
      (await addRewardBatch(rewards).then((resp) => {
        resp?.forEach((reward, index) => {
          importResults[Tab.REWARDS]?.push({
            ...reward.data,
            ...(reward?.rewardId ? { rewardId: reward.rewardId } : {}),
          });

          if (reward.rewardId) {
            return sortedResults[Tab.REWARDS]?.saved.push({
              rewardId: reward.rewardId,
              ...reward.data,
            });
          }
          sortedResults[Tab.REWARDS]?.unsaved.push({
            ...reward,
            importedId: index,
          });
        });
      }));

    achievements &&
      (await addAchievementBatch(formatImportedAchievements(achievements)).then(
        (resp) => {
          resp?.forEach((achievementData, index) => {
            if ("achievementId" in achievementData) {
              importResults[Tab.ACHIEVEMENTS]?.push({
                ...achievementData.data,
                achievementId: achievementData.achievementId,
              });

              sortedResults[Tab.ACHIEVEMENTS]?.saved.push({
                ...achievementData.data,
                achievementId: achievementData.achievementId,
              });
            } else {
              importResults[Tab.ACHIEVEMENTS]?.push({
                ...achievementData.data,
              });

              sortedResults[Tab.ACHIEVEMENTS]?.unsaved.push({
                ...achievementData,
                importedId: index,
              });
            }
          });
        },
      ));

    campaigns &&
      (await addCampaignBatch(formatImportedCampaigns(campaigns)).then(
        (resp) => {
          resp?.forEach((campaignData, index) => {
            if ("campaignId" in campaignData) {
              importResults[Tab.CAMPAIGNS]?.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);

              sortedResults[Tab.CAMPAIGNS]?.saved.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);
            } else {
              importResults[Tab.CAMPAIGNS]?.push({
                ...campaignData.data,
                importedId: index,
              });

              sortedResults[Tab.CAMPAIGNS]?.unsaved.push({
                ...campaignData,
                importedId: index,
              });
            }
          });
        },
      ));

    referrals &&
      (await addCampaignBatch(formatImportedCampaigns(referrals)).then(
        (resp) => {
          resp?.forEach((campaignData, index) => {
            if ("campaignId" in campaignData) {
              importResults[Tab.REFERRALS]?.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);

              sortedResults[Tab.REFERRALS]?.saved.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);
            } else {
              importResults[Tab.REFERRALS]?.push({
                ...campaignData.data,
                importedId: index,
              });

              sortedResults[Tab.REFERRALS]?.unsaved.push({
                ...campaignData,
                importedId: index,
              });
            }
          });
        },
      ));

    automations &&
      (await addCampaignBatch(formatImportedCampaigns(automations)).then(
        (resp) => {
          resp?.forEach((campaignData, index) => {
            if ("campaignId" in campaignData) {
              importResults[Tab.AUTOMATIONS]?.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);

              sortedResults[Tab.AUTOMATIONS]?.saved.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);
            } else {
              importResults[Tab.AUTOMATIONS]?.push({
                ...campaignData.data,
                importedId: index,
              });

              sortedResults[Tab.AUTOMATIONS]?.unsaved.push({
                ...campaignData,
                importedId: index,
              });
            }
          });
        },
      ));
    return { importResults, sortedResults };
  };

  return { addGlobalConfigBatch };
};
